// Typed client for the control Worker (docs/INTERFACES.md section 5). Same origin, cookie session.
// Dashboard endpoints at /api/clients/:id/d/<path> are served by the client's Tenant Durable Object (spec 3.3).

import type {
  AuditEntry,
  MessageRating,
  PreferenceRecord,
  Rule,
  RuleSuggestion,
  Calibration,
  CalibrationAnswer,
  ClientView,
  ConversationDetail,
  ConversationSummary,
  LeadFollowUpSettings,
  MeResponse,
  MessageView,
  Profile,
  ProfileData,
  FieldState,
  IssueDetail,
  IssueRow,
  IssueStatus,
  OperatorOverview,
  Situation,
  TodaySummary,
  TenantChannel,
  TenantNotice,
  UsageView,
} from '@lobby/shared'
import type { ChannelKind, ChannelStatus, MetaConnectBody, MetaPageOption, WhatsAppSignupConfig } from './lib/channels'
import type { ConversationFilter } from './lib/conversations'
import { asModelCalls } from './lib/modelCalls'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, details?: unknown) {
    super(code)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' }
  // The Worker's CSRF check requires JSON on every state-changing request, even without a payload.
  const write = method !== 'GET'
  if (write) headers['content-type'] = 'application/json'
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers,
      body: write ? JSON.stringify(body ?? {}) : undefined,
    })
  } catch {
    throw new ApiError(0, 'network_error')
  }
  const text = await res.text()
  let json: unknown
  if (text) {
    try {
      json = JSON.parse(text)
    } catch {
      json = undefined
    }
  }
  if (!res.ok) {
    const err = (json && typeof json === 'object' ? json : {}) as { error?: unknown; details?: unknown }
    throw new ApiError(res.status, typeof err.error === 'string' ? err.error : `http_${res.status}`, err.details)
  }
  return json as T
}

const enc = encodeURIComponent
const c = (id: string) => `/api/clients/${enc(id)}`
const d = (id: string, path: string) => `${c(id)}/d/${path}`

export interface CreateClientBody {
  businessName: string
  website: string
  ownerPhone: string
}

export interface PutProfileBody {
  data: ProfileData
  fieldStates: Record<string, FieldState>
}

export const api = {
  // Auth
  login: (email: string, website?: string | null) => request<{ ok: true }>('POST', '/auth/login', website ? { email, website } : { email }),
  /** Consumes a magic-link token. Only ever called from a button press, so link scanners (GET) can't use it up. */
  verify: (token: string) => request<{ ok: true }>('POST', '/auth/verify', { token }),
  logout: () => request<unknown>('POST', '/auth/logout'),
  me: () => request<MeResponse>('GET', '/api/me'),

  // Operator page (statistics only)
  operatorOverview: () => request<OperatorOverview>('GET', '/api/operator/overview'),
  issues: (status: IssueStatus | 'all') => request<{ issues: IssueRow[] }>('GET', `/api/operator/issues?status=${status}`),
  issue: (id: string) => request<IssueDetail>('GET', `/api/operator/issues/${encodeURIComponent(id)}`),
  setIssueStatus: (id: string, status: IssueStatus) => request<{ issue: IssueRow }>('POST', `/api/operator/issues/${encodeURIComponent(id)}`, { status }),

  // Client and onboarding
  createClient: (body: CreateClientBody) => request<ClientView>('POST', '/api/clients', body),
  client: (id: string) => request<ClientView>('GET', c(id)),
  generateProfile: (id: string) => request<Profile>('POST', `${c(id)}/profile/generate`),
  profile: (id: string) => request<Profile>('GET', `${c(id)}/profile`),
  saveProfile: (id: string, body: PutProfileBody) => request<Profile>('PUT', `${c(id)}/profile`, body),
  generateCalibration: (id: string) => request<Calibration>('POST', `${c(id)}/calibration/generate`),
  calibration: (id: string) => request<Calibration>('GET', `${c(id)}/calibration`),
  saveAnswers: (id: string, answers: CalibrationAnswer[]) =>
    request<Calibration>('PUT', `${c(id)}/calibration/answers`, { answers }),
  saveRules: (id: string, rules: { id?: string; text: string }[]) =>
    request<Calibration>('PUT', `${c(id)}/calibration/rules`, { rules }),
  rules: (id: string) => request<{ rules: Rule[]; preferences: PreferenceRecord[] }>('GET', `${c(id)}/rules`),
  deleteRule: (id: string, ruleId: string) => request<{ ok: true }>('DELETE', `${c(id)}/rules/${enc(ruleId)}`),
  modelCalls: async (id: string) => asModelCalls(await request<unknown>('GET', `${c(id)}/model-calls`)),
  situations: (id: string) => request<Situation[]>('GET', `${c(id)}/situations`),
  addSituation: (id: string, s: Omit<Situation, 'id'>) => request<Situation>('POST', `${c(id)}/situations`, s),
  deleteSituation: (id: string, sid: string) => request<{ ok: true }>('DELETE', `${c(id)}/situations/${enc(sid)}`),
  jobs: (id: string) => request<{ leadFollowUp: LeadFollowUpSettings }>('GET', `${c(id)}/jobs`),
  saveJobs: (id: string, leadFollowUp: LeadFollowUpSettings) =>
    request<{ leadFollowUp: LeadFollowUpSettings }>('PUT', `${c(id)}/jobs`, { leadFollowUp }),

  // Channels (Worker; access tokens never reach the browser). Spec 3.4.
  whatsappConfig: () => request<WhatsAppSignupConfig>('GET', '/api/channels/whatsapp/config'),
  /** Must run right after Embedded Signup: Meta's code expires 30 seconds after it is issued. */
  whatsappConnect: (body: { code: string; wabaId: string; phoneNumberId: string }) =>
    request<{ channel: ChannelStatus }>('POST', '/api/channels/whatsapp/connect', body),
  whatsappBillingConfirmed: () => request<{ channel: ChannelStatus }>('POST', '/api/channels/whatsapp/billing-confirmed'),
  whatsappDisconnect: () => request<unknown>('DELETE', '/api/channels/whatsapp'),
  /** Full-page navigation target: the Worker redirects to Facebook Login and back to `?meta=pick` or `?meta_error=`. */
  metaStartUrl: (returnTo: 'setup' | 'channels') => `/api/channels/meta/start?return=${returnTo}`,
  /** `pick`: the login state from the callback redirect (`?meta=pick&pick=<state>`); without it the latest login. */
  metaPages: (pick?: string | null) =>
    request<{ pages: MetaPageOption[] }>('GET', `/api/channels/meta/pages${pick ? `?pick=${enc(pick)}` : ''}`),
  metaConnect: (body: MetaConnectBody) => request<{ channels: ChannelStatus[] }>('POST', '/api/channels/meta/connect', body),
  metaDisconnect: (kind: Exclude<ChannelKind, 'whatsapp'>) => request<unknown>('DELETE', `/api/channels/meta/${kind}`),
  metaComments: (kind: Exclude<ChannelKind, 'whatsapp'>, comments: boolean) =>
    request<{ channel: ChannelStatus }>('PATCH', `/api/channels/meta/${kind}`, { comments }),

  // Billing and lifecycle
  checkout: (id: string) => request<{ url: string }>('POST', `${c(id)}/checkout`),
  portal: (id: string) => request<{ url: string }>('POST', `${c(id)}/portal`),
  goLive: (id: string) => request<ClientView>('POST', `${c(id)}/go-live`),
  pause: (id: string) => request<ClientView>('POST', `${c(id)}/pause`),
  resume: (id: string) => request<ClientView>('POST', `${c(id)}/resume`),

  // Dashboard data (Tenant Durable Object)
  summary: (id: string) => request<TodaySummary>('GET', d(id, 'summary')),
  conversations: (id: string, filter: ConversationFilter) =>
    request<ConversationSummary[]>('GET', d(id, `conversations?filter=${filter}`)),
  conversation: (id: string, cid: string) => request<ConversationDetail>('GET', d(id, `conversations/${enc(cid)}`)),
  takeover: (id: string, cid: string) => request<ConversationSummary>('POST', d(id, `conversations/${enc(cid)}/takeover`)),
  handback: (id: string, cid: string) => request<ConversationSummary>('POST', d(id, `conversations/${enc(cid)}/handback`)),
  booked: (id: string, cid: string, booked: boolean) => request<ConversationSummary>('POST', d(id, `conversations/${enc(cid)}/booked`), { booked }),
  send: (id: string, cid: string, text: string) =>
    request<MessageView>('POST', d(id, `conversations/${enc(cid)}/send`), { text }),
  channels: (id: string) => request<TenantChannel[]>('GET', d(id, 'channels')),
  notices: (id: string) => request<TenantNotice[]>('GET', d(id, 'notices')),
  dismissNotice: (id: string, nid: string) => request<{ ok: true }>('POST', d(id, `notices/${enc(nid)}/dismiss`)),
  usage: (id: string) => request<UsageView>('GET', d(id, 'usage')),
  audit: (id: string, cid: string) => request<AuditEntry[]>('GET', d(id, `conversations/${enc(cid)}/audit`)),
  rateMessage: (id: string, messageId: string, body: MessageRating) =>
    request<{ ok: true }>('POST', d(id, `messages/${enc(messageId)}/rating`), body),
  suggestions: (id: string) => request<RuleSuggestion[]>('GET', d(id, 'suggestions')),
  generateSuggestions: (id: string) => request<RuleSuggestion[]>('POST', d(id, 'suggestions/generate')),
  acceptSuggestion: (id: string, sid: string, text?: string) =>
    request<RuleSuggestion>('POST', d(id, `suggestions/${enc(sid)}/accept`), text ? { text } : {}),
  rejectSuggestion: (id: string, sid: string) => request<RuleSuggestion>('POST', d(id, `suggestions/${enc(sid)}/reject`)),

  /** WebSocket path for live events (LiveEvent JSON frames). Turn into a ws(s):// URL with `liveSocketUrl`. */
  livePath: (id: string) => d(id, 'live'),
  /** URL of the JSON export download. */
  exportUrl: (id: string) => d(id, 'export'),
}

export const isApiError = (e: unknown, status?: number): e is ApiError =>
  e instanceof ApiError && (status === undefined || e.status === status)

/** A sentence for the owner. Never shows raw stack traces. */
export function errorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Something went wrong. Please try again.'
  if (e.status === 0) return "Can't reach Lobby. Check your connection and try again."
  if (e.status === 401) return 'Your session has ended. Please sign in again.'
  if (e.status === 403 && e.code === 'bad_origin') return "This address isn't set up for signing in. Open Lobby from its main address and try again."
  if (e.status === 403) return "You don't have access to this business."
  if (e.status === 429 && e.code === 'rate_limited') return 'Daily limit for AI drafts reached; try again tomorrow.'
  if (e.status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (e.status === 409 && (e.code === 'messenger_unavailable' || e.code === 'window_closed'))
    return "Can't send in this chat right now. Meta allows replies only within 24 hours of the customer's last message, or the channel is disconnected."
  if (e.code === 'payment') return 'Meta refused the message because of a payment problem. Check the card in Meta Billing.'
  if (e.code === 'token_invalid') return 'Meta no longer accepts Lobby’s access. Reconnect the channel on the Channels page.'
  if (e.status === 409) return humanize(e.code, 'That was already done.')
  if (e.status === 422) return humanize(e.code, 'Some details are missing.')
  if (e.status === 400) return humanize(e.code, 'Please check the highlighted fields.')
  if (e.code === 'suggester_failed') return "The AI couldn't review your corrections this time. Lobby tries again tomorrow, or press the button again."
  if (e.code === 'generation_failed') return "The AI couldn't finish this time. Please try again."
  if (e.code === 'bad_origin' || e.code === 'content_type_must_be_json')
    return 'This request was blocked by a security check. Reload the page and try again.'
  if (e.code === 'tenant_not_ready') return 'Your assistant is still being set up. Please try again in a moment.'
  if (e.status >= 500) return 'Lobby had a problem on its side. Please try again in a moment.'
  return humanize(e.code, 'Something went wrong. Please try again.')
}

function humanize(code: string, fallback: string): string {
  if (!code || code.startsWith('http_')) return fallback
  // Worker errors are short snake_case codes or sentences.
  if (/^[a-z0-9_]+$/.test(code)) {
    const s = code.replace(/_/g, ' ')
    return s.charAt(0).toUpperCase() + s.slice(1) + '.'
  }
  return code
}
